/**
 * Staff roles + role-derived UI gating for the admin console.
 *
 * The role lives in Aurora `users.primary_role` and rides on the token as the
 * `https://re-platform/roles` claim — the Auth0 Action sets it on BOTH the id
 * and access tokens (see auth0-action-token-targeting), and the dev session JWT
 * (scripts/mint-dev-session-jwt.py) carries the same claim. The BACKEND is the
 * authority: every privileged route is `require_role`-gated and 403s a caller
 * who lacks it. This module only adapts the UI so staff don't see — or land on
 * — screens that would 403.
 *
 * Visibility model by role:
 *   super_admin     — everything (Tenants, Staff, Templates, Active, Billing).
 *   brokerage_admin — Staff + Templates + Active (own tenant); NO Tenants, NO
 *                     Billing (billing routes are super_admin-only).
 *   agent           — Dashboard + Active (their assigned clients only, scoped
 *                     server-side by resolve_agent_scope); admin-svc is
 *                     super/brokerage-gated, so no Tenants/Staff/Templates/Billing.
 *
 * When no role is known (dev with no token pasted, or a token missing the claim)
 * the UI does NOT gate — the backend still enforces, and gating-to-nothing would
 * be a dead-end.
 */

import { useActingTenant } from "./actingTenant";
import { useAuth } from "./auth";

export const ROLES_CLAIM = "https://re-platform/roles";

export type StaffRole = "super_admin" | "brokerage_admin" | "agent";

/** Highest-privilege first — drives default landing + labeling. */
const RANK: readonly StaffRole[] = ["super_admin", "brokerage_admin", "agent"];

/** Read the roles claim off a decoded token-claims object (no verification). */
export function rolesFromClaims(
  claims: Record<string, unknown> | null | undefined,
): string[] {
  const claim = claims?.[ROLES_CLAIM];
  return Array.isArray(claim)
    ? claim.filter((r): r is string => typeof r === "string")
    : [];
}

/** Decode a JWT's payload claims without verifying it (UI only). */
export function claimsFromToken(
  token: string | null,
): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Decode the roles claim from a JWT string without verifying it (UI only). */
export function rolesFromToken(token: string | null): string[] {
  if (!token) return [];
  try {
    const part = token.split(".")[1];
    if (!part) return [];
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return rolesFromClaims(JSON.parse(json) as Record<string, unknown>);
  } catch {
    return [];
  }
}

export interface RoleInfo {
  roles: string[];
  isSuperAdmin: boolean;
  isBrokerageAdmin: boolean;
  isAgent: boolean;
  /** Highest-privilege role held, or null if none recognized. */
  primary: StaffRole | null;
  /** True when no role is known — UI should not gate (backend still enforces). */
  unknown: boolean;
  /** True when the caller holds at least one of `allow`, OR no role is known. */
  allows: (allow: readonly StaffRole[]) => boolean;
}

export function deriveRoles(roles: readonly string[]): RoleInfo {
  const set = new Set(roles);
  const unknown = roles.length === 0;
  return {
    roles: [...roles],
    isSuperAdmin: set.has("super_admin"),
    isBrokerageAdmin: set.has("brokerage_admin"),
    isAgent: set.has("agent"),
    primary: RANK.find((r) => set.has(r)) ?? null,
    unknown,
    allows: (allow) => unknown || allow.some((r) => set.has(r)),
  };
}

export function useRoles(): RoleInfo {
  const { roles } = useAuth();
  // Dual-licensed staff acting in another tenant: the backend swaps their
  // effective role to the membership row's role, so the UI must gate on that,
  // not the token claim (which reflects the login-org tenant only).
  //
  // Read from CONTEXT, not localStorage: since PR 4 switching brokerage
  // re-renders instead of reloading the page, so a localStorage read here
  // would keep gating on the previous tenant's role until a manual refresh.
  const { acting } = useActingTenant();
  return deriveRoles(acting ? [acting.role] : roles);
}
